import { multiplyMoney, roundMoney, sumMoney } from './money';
import { resolveEstimateRate, type EstimateRates } from './estimate.rates';

export type MarginShieldRisk = 'H' | 'M' | 'L';

export interface MarginShieldEstimateItem {
  id?: string;
  category?: string;
  label?: string;
  name?: string;
  description?: string;
}

export interface MarginShieldAuditInput {
  workType?: string;
  description?: string;
  items?: Array<string | MarginShieldEstimateItem>;
  area?: number | null;
  curbLengthM?: number;
  region?: string;
  userRates?: EstimateRates;
}

export interface MarginShieldImpact {
  type: 'flat' | 'perM2' | 'perMb';
  amountNet: number;
  fallbackNet?: number;
}

export interface MarginShieldRule {
  id: string;
  category: string[];
  triggerKeywords: string[];
  includedKeywords: string[];
  missingItem: string;
  estimatedImpact: MarginShieldImpact;
  risk: MarginShieldRisk;
  alertMessage: string;
  triggerMode?: 'any' | 'all';
}

export interface MarginShieldAlert {
  quantity?: number;
  id: string;
  ruleId: string;
  category: string[];
  missingItem: string;
  estimatedImpact: MarginShieldImpact;
  estimatedHiddenLoss: number;
  risk: MarginShieldRisk;
  alertMessage: string;
  actionRequired: boolean;
}

export interface MarginShieldAuditResult {
  alerts: MarginShieldAlert[];
  missingItems: string[];
  totalHiddenLoss: number;
  isSafe: boolean;
  actionRequired: boolean;
  userMessages: string[];
}

export const MARGIN_SHIELD_AUDIT_RULES: MarginShieldRule[] = [
  {
    id: 'missing_primer',
    category: ['painting', 'plaster', 'general'],
    triggerKeywords: ['malowanie', 'gładź', 'gladz', 'szpachlowanie'],
    includedKeywords: [
      'grunt',
      'gruntowanie',
      'grunt akrylowy',
      'podkład',
      'podklad',
      'warstwa gruntująca',
      'preparat gruntujący',
      'primer',
    ],
    missingItem: 'Gruntowanie podłoża',
    estimatedImpact: { type: 'perM2', amountNet: 8, fallbackNet: 120 },
    risk: 'M',
    alertMessage: 'Sprawdź, czy uwzględniłeś gruntowanie podłoża przed dalszymi pracami.',
  },
  {
    id: 'missing_surface_protection',
    category: ['painting', 'plaster', 'general'],
    triggerKeywords: ['malowanie', 'gładź', 'gladz', 'szpachlowanie', 'remont'],
    includedKeywords: [
      'folia',
      'folia malarska',
      'taśma',
      'tasma',
      'taśmy',
      'tasmy',
      'zabezpieczenie',
      'zabezpieczenie powierzchni',
      'oklejanie',
    ],
    missingItem: 'Zabezpieczenie powierzchni folią i taśmami',
    estimatedImpact: { type: 'perM2', amountNet: 4, fallbackNet: 80 },
    risk: 'M',
    alertMessage: 'Może brakować zabezpieczenia podłóg, listew albo stolarki folią i taśmami.',
  },
  {
    id: 'missing_waterproofing',
    category: ['bathroom', 'tiling'],
    triggerKeywords: ['łazienka', 'lazienka', 'płytki', 'plytki'],
    includedKeywords: [
      'hydroizolacja',
      'folia w płynie',
      'folia w plynie',
      'hydroizolacja płynna',
      'hydroizolacja plynna',
      'izolacja płynna',
      'izolacja plynna',
      'uszczelnienie strefy mokrej',
      'masa uszczelniająca',
      'masa uszczelniajaca',
    ],
    missingItem: 'Hydroizolacja w strefie mokrej',
    estimatedImpact: { type: 'perM2', amountNet: 45, fallbackNet: 250 },
    risk: 'H',
    alertMessage: 'Sprawdź, czy w łazience doliczyłeś hydroizolację stref mokrych.',
    triggerMode: 'all',
  },
  {
    id: 'optional_jolly_edges',
    category: ['bathroom', 'tiling'],
    triggerKeywords: ['płytki', 'plytki', 'glazura', 'gres'],
    includedKeywords: [
      'szlif 45',
      'szlify 45',
      'jolly',
      'naroże 45',
      'naroze 45',
      'cięcie 45',
      'ciecie 45',
      'fazowanie płytek',
      'fazowanie plytek',
    ],
    missingItem: 'Szlify 45° / jolly na narożnikach',
    estimatedImpact: { type: 'flat', amountNet: 180 },
    risk: 'M',
    alertMessage: 'Warto potwierdzić, czy narożniki płytek wymagają szlifów 45° / jolly.',
  },
  {
    id: 'missing_sanitary_silicone',
    category: ['bathroom', 'tiling'],
    triggerKeywords: ['łazienka', 'lazienka', 'płytki', 'plytki', 'prysznic', 'wanna'],
    includedKeywords: [
      'silikon',
      'silikon sanitarny',
      'silikonowanie',
      'uszczelnienie silikonem',
      'fuga silikonowa',
    ],
    missingItem: 'Silikon sanitarny',
    estimatedImpact: { type: 'flat', amountNet: 120 },
    risk: 'M',
    alertMessage: 'Sprawdź, czy końcowe silikonowanie jest osobno ujęte w wycenie.',
  },
  {
    id: 'missing_waste_cleanup',
    category: ['painting', 'plaster', 'bathroom', 'tiling', 'paving', 'general'],
    triggerKeywords: [
      'remont',
      'łazienka',
      'lazienka',
      'płytki',
      'plytki',
      'malowanie',
      'gładź',
      'gladz',
      'bruk',
      'rozbiórka',
      'rozbiorka',
    ],
    includedKeywords: [
      'odpady',
      'wywóz',
      'wywoz',
      'sprzątanie',
      'sprzatanie',
      'gruz',
      'utylizacja',
      'kontener',
      'big bag',
      'porządkowanie',
      'porzadkowanie',
    ],
    missingItem: 'Odpady / wywóz / sprzątanie',
    estimatedImpact: { type: 'flat', amountNet: 150 },
    risk: 'M',
    alertMessage: 'Może brakować pozycji na odpady, wywóz albo sprzątanie po pracach.',
  },
  {
    id: 'missing_cutting_waste',
    category: ['bathroom', 'tiling', 'paving', 'general'],
    triggerKeywords: ['płytki', 'plytki', 'gres', 'panele', 'bruk', 'kostka'],
    includedKeywords: [
      'docinki',
      'zapas',
      'naddatek',
      'odpady materiałowe',
      'odpady materialowe',
      'rezerwa materiału',
      'rezerwa materialu',
      'zapas materiału',
      'zapas materialu',
      'ubytki materiałowe',
      'ubytki materialowe',
    ],
    missingItem: 'Docinki i zapas materiału',
    estimatedImpact: { type: 'perM2', amountNet: 6, fallbackNet: 100 },
    risk: 'L',
    alertMessage: 'Warto doliczyć zapas materiału i docinki, jeśli praca wymaga cięcia.',
  },
  {
    id: 'paving_excavation',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['korytowanie', 'koryto', 'wykop', 'przygotowanie terenu'],
    missingItem: 'Przygotowanie / korytowanie terenu',
    estimatedImpact: { type: 'perM2', amountNet: 15, fallbackNet: 200 },
    risk: 'M',
    alertMessage: 'Sprawdź, czy zakres obejmuje korytowanie terenu. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_leveling',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['plantowanie', 'wyrównanie', 'niwelacja'],
    missingItem: 'Plantowanie / wyrównanie',
    estimatedImpact: { type: 'perM2', amountNet: 8, fallbackNet: 120 },
    risk: 'L',
    alertMessage: 'Sprawdź, czy teren wymaga plantowania i wyrównania. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_geotextile',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['geowłóknina', 'geotkanina', 'separacja'],
    missingItem: 'Geowłóknina',
    estimatedImpact: { type: 'perM2', amountNet: 6, fallbackNet: 100 },
    risk: 'L',
    alertMessage: 'Sprawdź, czy podłoże wymaga geowłókniny. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_base',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['podbudowa', 'kruszywo', 'tłuczeń', 'stabilizacja'],
    missingItem: 'Podbudowa z kruszywa',
    estimatedImpact: { type: 'perM2', amountNet: 40, fallbackNet: 400 },
    risk: 'M',
    alertMessage: 'Sprawdź, czy zakres obejmuje podbudowę z kruszywa. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_compaction',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['zagęszczenie', 'zagęszczanie', 'ubijanie', 'zagęszczarka'],
    missingItem: 'Zagęszczenie warstw',
    estimatedImpact: { type: 'perM2', amountNet: 8, fallbackNet: 120 },
    risk: 'L',
    alertMessage: 'Sprawdź, czy zakres obejmuje zagęszczenie warstw. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_bedding',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['podsypka', 'cementowo-piaskowa', 'cementowo piaskowa'],
    missingItem: 'Podsypka cementowo-piaskowa',
    estimatedImpact: { type: 'perM2', amountNet: 12, fallbackNet: 150 },
    risk: 'L',
    alertMessage: 'Sprawdź, czy zakres obejmuje podsypkę cementowo-piaskową. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_edges',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['obrzeża', 'krawężniki', 'opornik'],
    missingItem: 'Obrzeża / krawężniki',
    estimatedImpact: { type: 'flat', amountNet: 300 },
    risk: 'M',
    alertMessage: 'Sprawdź, czy zakres obejmuje obrzeża lub krawężniki. Jeśli tak, dodaj tę pozycję do ceny.',
  },
  {
    id: 'paving_material_transport',
    category: ['paving'],
    triggerKeywords: ['bruk', 'kostka', 'brukarstwo'],
    includedKeywords: ['transport materiału', 'dowóz', 'transport kostki'],
    missingItem: 'Transport materiału',
    estimatedImpact: { type: 'flat', amountNet: 300 },
    risk: 'L',
    alertMessage: 'Sprawdź, czy zakres obejmuje transport materiału. Jeśli tak, dodaj tę pozycję do ceny.',
  },
];

const WORK_TYPE_ALIASES: Record<string, string> = {
  bathroom: 'łazienka płytki glazura gres',
  lazience: 'łazienka płytki glazura gres',
  lazienka: 'łazienka płytki glazura gres',
  tiling: 'płytki glazura gres',
  painting: 'malowanie',
  malarz: 'malowanie',
  plaster: 'gładź szpachlowanie',
  gladz: 'gładź szpachlowanie',
  paving: 'bruk kostka brukarstwo',
  brukarstwo: 'bruk kostka brukarstwo',
  general: 'remont',
};

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/m²/g, 'm2')
    .replace(/[^a-z0-9+% ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function itemToText(item: string | MarginShieldEstimateItem): string {
  if (typeof item === 'string') return item;
  return [item.id, item.category, item.label, item.name, item.description].filter(Boolean).join(' ');
}

function containsKeyword(text: string, keyword: string): boolean {
  const normalizedKeyword = normalizeText(keyword);
  return normalizedKeyword.length > 0 && text.includes(normalizedKeyword);
}

function containsAny(text: string, keywords: string[]): boolean {
  return keywords.some((keyword) => containsKeyword(text, keyword));
}

function ruleIsTriggered(rule: MarginShieldRule, sourceText: string): boolean {
  if (rule.triggerMode === 'all') {
    return rule.triggerKeywords.every((keyword) => containsKeyword(sourceText, keyword));
  }

  return containsAny(sourceText, rule.triggerKeywords);
}

function calculateHiddenLoss(rule: MarginShieldRule, area?: number | null): number {
  if (rule.estimatedImpact.type === 'flat') {
    return roundMoney(rule.estimatedImpact.amountNet);
  }

  if (typeof area === 'number' && Number.isFinite(area) && area > 0) {
    return multiplyMoney(area, rule.estimatedImpact.amountNet);
  }

  return roundMoney(rule.estimatedImpact.fallbackNet ?? 0);
}

export function runMarginShieldAudit(input: MarginShieldAuditInput): MarginShieldAuditResult {
  const normalizedWorkType = normalizeText(input.workType ?? 'general');
  const workTypeAlias = WORK_TYPE_ALIASES[normalizedWorkType] ?? normalizedWorkType;
  const descriptionText = normalizeText(input.description ?? '');
  const itemText = normalizeText((input.items ?? []).map(itemToText).join(' '));
  const sourceText = normalizeText([normalizedWorkType, workTypeAlias, descriptionText, itemText].join(' '));

  const alerts = MARGIN_SHIELD_AUDIT_RULES
    .filter((rule) => ruleIsTriggered(rule, sourceText))
    .filter((rule) => !containsAny(itemText, rule.includedKeywords))
    .map((rule): MarginShieldAlert => {
      // Use the contractor's explicit measurement, never an inferred perimeter.
      const quantity = rule.id === 'paving_edges' && Number.isFinite(input.curbLengthM) && (input.curbLengthM ?? 0) > 0
        ? input.curbLengthM : undefined;
      const estimatedImpact: MarginShieldImpact = quantity !== undefined
        ? { type: 'perMb', amountNet: resolveEstimateRate('paving_edges', input.region, input.userRates) }
        : rule.estimatedImpact;
      const estimatedHiddenLoss = quantity !== undefined
        ? multiplyMoney(quantity, estimatedImpact.amountNet)
        : calculateHiddenLoss(rule, input.area);
      const actionRequired = rule.risk === 'H';

      return {
        id: rule.id,
        ruleId: rule.id,
        category: rule.category,
        missingItem: rule.missingItem,
        estimatedImpact,
        ...(quantity !== undefined ? { quantity } : {}),
        estimatedHiddenLoss,
        risk: rule.risk,
        alertMessage: rule.alertMessage,
        actionRequired,
      };
    });

  const actionRequired = alerts.some((alert) => alert.actionRequired);

  return {
    alerts,
    missingItems: alerts.map((alert) => alert.missingItem),
    totalHiddenLoss: sumMoney(alerts.map((alert) => alert.estimatedHiddenLoss)),
    isSafe: !actionRequired,
    actionRequired,
    userMessages: alerts.map((alert) => alert.alertMessage),
  };
}
